import * as React from 'react'
import { Download, FileText, Table, Code, Clipboard, FileJson } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  toCSV,
  toTSV,
  toJSON,
  toLaTeX,
  toPython,
  downloadString,
} from '@/lib/dataExportUtils'

interface DataExportMenuProps {
  data: Record<string, unknown>[] | number[] | { x: number; y: number }[]
  fileName: string
  columns?: string[]
  title?: string
  className?: string
}

export function DataExportMenu({
  data,
  fileName,
  columns,
  title,
  className,
}: DataExportMenuProps) {
  const [open, setOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const exportData = data as Record<string, unknown>[] | number[]

  const handleCSV = () => {
    const content = toCSV(exportData, columns)
    downloadString(content, `${fileName}.csv`, 'text/csv')
    setOpen(false)
  }

  const handleTSV = () => {
    const content = toTSV(exportData, columns)
    downloadString(content, `${fileName}.tsv`, 'text/tab-separated-values')
    setOpen(false)
  }

  const handleJSON = () => {
    const content = toJSON(exportData)
    downloadString(content, `${fileName}.json`, 'application/json')
    setOpen(false)
  }

  const handleLaTeX = () => {
    const content = toLaTeX(exportData, columns, title)
    downloadString(content, `${fileName}.tex`, 'text/x-latex')
    setOpen(false)
  }

  const handlePython = () => {
    const content = toPython(exportData, columns)
    downloadString(content, `${fileName}.py`, 'text/x-python')
    setOpen(false)
  }

  const handleCopy = async () => {
    const content = toCSV(exportData, columns)
    await navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    setOpen(false)
  }

  if (!data || !data.length) return null

  return (
    <div ref={ref} className={cn('relative inline-block', className)}>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(!open)}
        title="Export data"
      >
        <Download className="h-3.5 w-3.5" />
      </Button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-44 rounded-md border bg-popover p-1 shadow-md">
          <button
            type="button"
            onClick={handleCSV}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
          >
            <Table className="h-3.5 w-3.5" />
            CSV
          </button>
          <button
            type="button"
            onClick={handleTSV}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
          >
            <Table className="h-3.5 w-3.5" />
            TSV
          </button>
          <button
            type="button"
            onClick={handleJSON}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
          >
            <FileJson className="h-3.5 w-3.5" />
            JSON
          </button>
          <button
            type="button"
            onClick={handleLaTeX}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
          >
            <FileText className="h-3.5 w-3.5" />
            LaTeX
          </button>
          <button
            type="button"
            onClick={handlePython}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
          >
            <Code className="h-3.5 w-3.5" />
            Python (NumPy)
          </button>
          <div className="my-0.5 h-px bg-border" />
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
          >
            <Clipboard className="h-3.5 w-3.5" />
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>
      )}
    </div>
  )
}
